import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { api, API, formatApiError } from "@/lib/api";
import { RecordsTable } from "@/components/RecordsTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FILTERS, SORTS } from "@/lib/constants";
import { toast } from "sonner";
import { Search, Download, Plus, Inbox } from "lucide-react";

const TITLES = { invoices: "Invoices", payouts: "Payouts", all: "Ledger" };

export default function Ledger({ scope = "all" }) {
  const { openCreate, triggerRefresh, refreshKey } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState(searchParams.get("filter") || (scope === "invoices" ? "invoices" : scope === "payouts" ? "payouts" : "all"));
  const [sort, setSort] = useState(searchParams.get("sort") || "date");
  const [q, setQ] = useState(searchParams.get("q") || "");

  useEffect(() => {
    const p = {};
    if (filter) p.filter = filter;
    if (sort) p.sort = sort;
    if (q) p.q = q;
    setSearchParams(p, { replace: true });
  }, [filter, sort, q, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      let data = [];
      if (scope === "invoices") data = (await api.get("/invoices")).data;
      else if (scope === "payouts") data = (await api.get("/payouts")).data;
      else {
        const [inv, pay] = await Promise.all([api.get("/invoices"), api.get("/payouts")]);
        data = [...inv.data, ...pay.data];
      }
      setRecords(data);
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [scope]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const doAction = async (record, action) => {
    try {
      const base = record.type === "invoice" ? "invoices" : "payouts";
      await api.post(`/${base}/${record.id}/${action}`);
      toast.success("Record updated");
      load(); triggerRefresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    let list = [...records];
    if (filter === "invoices") list = list.filter((r) => r.type === "invoice");
    else if (filter === "payouts") list = list.filter((r) => r.type === "payout");
    else if (filter === "upcoming") list = list.filter((r) => ["Upcoming", "Pending"].includes(r.display_status));
    else if (filter === "due") list = list.filter((r) => r.display_status === "Due Today");
    else if (filter === "overdue") list = list.filter((r) => ["Overdue", "Expected payout missed"].includes(r.display_status));
    else if (filter === "paid") list = list.filter((r) => ["Paid", "Received"].includes(r.display_status));

    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((r) => r.reference_code.toLowerCase().includes(s) || r.name.toLowerCase().includes(s));
    }
    const cmp = {
      date: (a, b) => (a.date < b.date ? -1 : 1),
      amount: (a, b) => b.amount_cents - a.amount_cents,
      newest: (a, b) => (a.created_at < b.created_at ? 1 : -1),
      oldest: (a, b) => (a.created_at < b.created_at ? -1 : 1),
      status: (a, b) => a.display_status.localeCompare(b.display_status),
    }[sort];
    return list.sort(cmp);
  }, [records, filter, sort, q]);

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/export.csv`, { credentials: "include" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "paysignal-records.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">{TITLES[scope]}</h1>
          <p className="text-espresso-soft mt-1">{filtered.length} record{filtered.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn"><Download size={16} className="mr-1" /> Export CSV</Button>
          <Button onClick={() => openCreate(null)} data-testid="ledger-new-btn"><Plus size={16} className="mr-1" /> New</Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-warmmuted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reference, client or platform…" className="pl-9" data-testid="ledger-search" />
        </div>
        {scope === "all" && (
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="sm:w-48" data-testid="ledger-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{FILTERS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="sm:w-48" data-testid="ledger-sort"><SelectValue /></SelectTrigger>
          <SelectContent>{SORTS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {scope !== "all" && (
        <div className="flex flex-wrap gap-2" data-testid="quick-filters">
          {["all", "upcoming", "due", "overdue", "paid"].map((k) => {
            const f = FILTERS.find((x) => x.key === k);
            const active = filter === k || (k === "all" && filter === scope);
            return (
              <button key={k} onClick={() => setFilter(k === "all" ? scope : k)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active ? "bg-espresso text-cream border-espresso" : "bg-white text-espresso-soft hover:bg-oatmeal"}`}
                data-testid={`chip-${k}`}>
                {f.label}
              </button>
            );
          })}
        </div>
      )}

      {loading ? <Skeleton className="h-64 rounded-lg" />
        : error ? <div className="border border-rose-200 bg-rose-50 text-rose-700 rounded-lg p-6" data-testid="ledger-error">{error}</div>
        : filtered.length === 0 ? (
          <div className="border rounded-2xl bg-white p-12 text-center" data-testid="ledger-empty">
            <span className="inline-grid place-items-center w-14 h-14 rounded-2xl bg-brandblue-light mx-auto mb-4"><Inbox size={26} className="text-brandblue" /></span>
            <h3 className="font-heading text-xl font-bold">No records here</h3>
            <p className="text-espresso-soft mt-1">Try a different filter, or add a new record.</p>
            <Button className="mt-5" onClick={() => openCreate(null)}><Plus size={16} className="mr-1" /> Add record</Button>
          </div>
        ) : <RecordsTable records={filtered} onAction={doAction} onEdit={openCreate} onRefresh={() => { load(); triggerRefresh(); }} />}
    </div>
  );
}
